import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { integrationQueryKeys } from './integrationQueryKeys';
import { StateTransitionParams, StateTransitionResponse } from 'IntegrationModule/types';
import { IntegrationState } from 'types/integration';
import { transitionIntegrationStateApi } from '../transitionIntegrationStateApi';

const SUCCESS_MESSAGES: Record<IntegrationState, string> = {
  [IntegrationState.ACTIVE]: 'Integration activated',
  [IntegrationState.SUSPENDED]: 'Integration suspended',
  [IntegrationState.ARCHIVED]: 'Integration archived',
  [IntegrationState.DRAFT]: 'Integration moved to draft',
};

export const useTransitionStateMutation = () => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<StateTransitionResponse>,
    AxiosResponse<{ error: string; message: string; statusCode: number }>,
    StateTransitionParams
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return transitionIntegrationStateApi({ params, token }) as Promise<
        AxiosResponse<StateTransitionResponse>
      >;
    },
    onSuccess: (_data, variables) => {
      showSuccess(SUCCESS_MESSAGES[variables.newState] || 'Integration state updated');
      queryClient.invalidateQueries({ queryKey: integrationQueryKeys.lists() });
    },
    onError: (error) => {
      showError(`Failed to update integration state: ${error?.data?.message || 'Unknown error'}`);
    },
  });

  return {
    transitionState: mutation.mutate,
    transitionStateAsync: mutation.mutateAsync,
    isTransitioning: mutation.isPending,
  };
};
